/* import __COLOCATED_TEMPLATE__ from './final-startlist-interface.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TrackedArray } from 'tracked-built-ins';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default class HeatStartlistComponent extends Component 
{
	@service store;
	@service csv;
	@service sprintTools;
	
	@tracked semifinalsEvent;
	
	@tracked startTimeA;
	@tracked startTimeB;
	
	@tracked S1ClassGroup;
	@tracked S2ClassGroup;
	
	@tracked AClassGroup;
	@tracked BClassGroup;
	
	@tracked AAssignments;
	@tracked BAssignments;
	
	@tracked LL1;
	@tracked LL2;
	@tracked LLOthers;
	
	@action
	async semifinalsEventChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.semifinalsEvent = null;
		}
		else
		{
			var event = await this.store.findRecord('Event', browserEvent.target.value);
			this.semifinalsEvent = event;
		}
		
		if(this.semifinalsEvent?.id)
		{
			await this.sprintTools.fetchResultsForEvent(this.semifinalsEvent.id);
		}
		this.S1ClassGroup = null;
		this.S2ClassGroup = null;
	}
	
	@action
	async S1ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S1ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S1ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async S2ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S2ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S2ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async AClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.AClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.startTimeA = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.AClassGroup = classGroup;
		}
	}
	
	@action
	async BClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.BClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.startTimeB = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.BClassGroup = classGroup;
		}
	}
	
	@action
	exportHeatStartListCSV()
	{
		// Check if assignments exist before exporting
		if (!this.AAssignments || !this.BAssignments) {
			return;
		}

		var buildData = (assignments, classGroup, startTime) => {
			var data = [...this.sprintTools.getCSVHeaders()];
			for (const result of assignments) {
				data.push(this.sprintTools.buildCSVRow(result, classGroup, startTime, result.bib));
			}
			return data;
		};

		this.csv.export(buildData(this.AAssignments, this.AClassGroup, this.startTimeA), {fileName:'AfinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.BAssignments, this.BClassGroup, this.startTimeB), {fileName:'BfinalsStartlist.csv',withSeparator:false});
	}
	
	@action
	async createClasses()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.AClassGroup, this.AAssignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.BClassGroup, this.BAssignments);
	}
	
	@action
	async createEntries()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationEntries = await destinationEvent.get('entries');
		var destinationClasses = await destinationEvent.get('classes');

		// useNewBib = false because this file uses result.bib
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.AClassGroup, this.AAssignments, this.startTimeA, false);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.BClassGroup, this.BAssignments, this.startTimeB, false);
	}
	
	async calculateLuckyLosers()
	{
		var S1Results = [...(await this.S1ClassGroup.resultsSorted)];
		var S2Results = [...(await this.S2ClassGroup.resultsSorted)];

		// Collect all 3rd/4th place candidates with valid times
		var luckyLoserCandidates = [];
		var addCandidate = function(result) {
			if(result && result.status == 'OK' && result.totalTimeInMilliseconds != null) {
				luckyLoserCandidates.push(result);
			}
		};

		// Add 3rd and 4th place from each semifinal
		addCandidate(S1Results[2]);
		addCandidate(S1Results[3]);
		addCandidate(S2Results[2]);
		addCandidate(S2Results[3]);

		// Sort by time and take fastest 2 as lucky losers
		luckyLoserCandidates.sort((a, b) => a.totalTimeInMilliseconds - b.totalTimeInMilliseconds);

		this.LL1 = luckyLoserCandidates[0] || null;
		this.LL2 = luckyLoserCandidates[1] || null;

		// Remaining candidates are unlucky losers
		var LLOthers = new TrackedArray();
		for(var i = 2; i < luckyLoserCandidates.length; i++) {
			LLOthers.push(luckyLoserCandidates[i]);
		}
		this.LLOthers = LLOthers;
	}
	
	async calculateHeatAssignments()
	{
		var AAssignments = [];
		var BAssignments = [];
		
		var S1ClassGroup = this.S1ClassGroup;
		var S2ClassGroup = this.S2ClassGroup;
		
		if(	S1ClassGroup == null ||
			S2ClassGroup == null)
		{
			return;
		}
		
		await this.calculateLuckyLosers();
		var S1Results = [...(await S1ClassGroup.resultsSorted)];
		var S2Results = [...(await S2ClassGroup.resultsSorted)];
		
		var LL1 = this.LL1;
		var LL2 = this.LL2;
		
		if(S1Results[0].bib < S2Results[0].bib)
		{
			S1Results[0].set('heatStartOrder',1);
			S2Results[0].set('heatStartOrder',2);
			AAssignments.push(S1Results[0]);
			AAssignments.push(S2Results[0]);
		}
		else
		{
			S1Results[0].set('heatStartOrder',2);
			S2Results[0].set('heatStartOrder',1);
			AAssignments.push(S2Results[0]);
			AAssignments.push(S1Results[0]);
		}
		
		if(S1Results[1].bib < S2Results[1].bib)
		{
			S1Results[1].set('heatStartOrder',3);
			S2Results[1].set('heatStartOrder',4);
			AAssignments.push(S1Results[1]);
			AAssignments.push(S2Results[1]);
		}
		else
		{
			S1Results[1].set('heatStartOrder',4);
			S2Results[1].set('heatStartOrder',3);
			AAssignments.push(S2Results[1]);
			AAssignments.push(S1Results[1]);
		}

		// B final: unlucky losers (3rd/4th who aren't LLs) + 5th/6th place
		// Collect unlucky losers (positions 1-2 in B final)
		var unluckyLosers = [];
		if(S1Results[2] != LL1 && S1Results[2] != LL2)
			unluckyLosers.push(S1Results[2]);
		if(S1Results[3] != LL1 && S1Results[3] != LL2)
			unluckyLosers.push(S1Results[3]);
		if(S2Results[2] != LL1 && S2Results[2] != LL2)
			unluckyLosers.push(S2Results[2]);
		if(S2Results[3] != LL1 && S2Results[3] != LL2)
			unluckyLosers.push(S2Results[3]);
		unluckyLosers.sort((a, b) => a.bib - b.bib);

		// Positions 1-2: unlucky losers sorted by bib
		for(var i = 0; i < unluckyLosers.length; i++) {
			unluckyLosers[i].set('heatStartOrder', i + 1);
			BAssignments.push(unluckyLosers[i]);
		}

		// Positions 3-4: 5th place finishers sorted by bib
		var fifthPlace = [S1Results[4], S2Results[4]];
		fifthPlace.sort((a, b) => a.bib - b.bib);
		fifthPlace[0].set('heatStartOrder', 3);
		fifthPlace[1].set('heatStartOrder', 4);
		BAssignments.push(fifthPlace[0]);
		BAssignments.push(fifthPlace[1]);

		// Positions 5-6: 6th place finishers sorted by bib
		var sixthPlace = [S1Results[5], S2Results[5]];
		sixthPlace.sort((a, b) => a.bib - b.bib);
		sixthPlace[0].set('heatStartOrder', 5);
		sixthPlace[1].set('heatStartOrder', 6);
		BAssignments.push(sixthPlace[0]);
		BAssignments.push(sixthPlace[1]);
			
		//lucky losers - assign by semifinal time
		//LL1 is faster (already sorted by totalTimeInMilliseconds), LL2 is slower
		LL1.set('heatStartOrder', 5);  // faster gets position 5
		LL2.set('heatStartOrder', 6);  // slower gets position 6
		AAssignments.push(LL1);
		AAssignments.push(LL2);
		
		this.AAssignments = AAssignments;
		this.BAssignments = BAssignments
	}
	
}
